import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatDate } from '@/utils/dateUtils';
import { getServiceStatusBadge, getHealthBadge } from '@/utils/badgeUtils';
import { MSAService } from '@/types';
import { Trash2, CreditCard, KeyRound, Pause, Play, Eye, EyeOff } from 'lucide-react';
import { CATEGORY_LABELS, ServiceCategory, getServiceIcon } from '@/constants/serviceCategories';
import { HealthStatus } from '@/types/health';

interface ServiceTableViewProps {
  services: MSAService[];
  healthStatuses: Record<number, HealthStatus>;
  isReadOnly?: boolean;
  selectedIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  onApproveClick: (serviceId: number) => void;
  onReject: (id: number, name: string) => void;
  onSuspend: (id: number, name: string) => void;
  onResume: (id: number, name: string) => void;
  onDelete: (id: number, name: string) => void;
  onRestore?: (id: number, name: string) => void;
  onCancelRejection?: (id: number, name: string) => void;
  onToggleVisibility: (id: number, name: string, newVisibility: boolean) => void;
  onServiceClick?: (service: MSAService) => void;
  onManagePlans?: (service: MSAService) => void;
  onManageClient?: (service: MSAService) => void;
}

export const ServiceTableView: React.FC<ServiceTableViewProps> = ({
  services,
  healthStatuses,
  isReadOnly = false,
  selectedIds,
  onSelectionChange,
  onApproveClick,
  onReject,
  onSuspend,
  onResume,
  onDelete,
  onRestore,
  onCancelRejection,
  onToggleVisibility,
  onServiceClick,
  onManagePlans,
  onManageClient,
}) => {
  const showCheckboxes = !!selectedIds && !!onSelectionChange;
  const selectableServices = services.filter(s => s.status === 'pending' || s.status === 'approved' || s.status === 'suspended');
  const allSelected = showCheckboxes && selectableServices.length > 0 && selectableServices.every(s => selectedIds.has(s.id));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableServices.map(s => s.id)));
    }
  };

  const handleToggle = (id: number) => {
    if (!onSelectionChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            {showCheckboxes && (
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-gray-300"
                  title="전체 선택/해제"
                />
              </TableHead>
            )}
            <TableHead className="whitespace-nowrap">ID</TableHead>
            <TableHead className="whitespace-nowrap">공개</TableHead>
            <TableHead className="whitespace-nowrap">카테고리</TableHead>
            <TableHead>서비스</TableHead>
            <TableHead>설명</TableHead>
            <TableHead className="whitespace-nowrap">개발사</TableHead>
            <TableHead className="whitespace-nowrap">이동</TableHead>
            <TableHead className="whitespace-nowrap">버전</TableHead>
            <TableHead className="whitespace-nowrap">상태</TableHead>
            <TableHead className="whitespace-nowrap">헬스체크</TableHead>
            <TableHead className="whitespace-nowrap">등록일</TableHead>
            {!isReadOnly && <TableHead className="whitespace-nowrap">작업</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {services.map((service) => (
            <TableRow key={service.id}>
              {showCheckboxes && (
                <TableCell>
                  {(service.status === 'pending' || service.status === 'approved' || service.status === 'suspended') ? (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(service.id)}
                      onChange={() => handleToggle(service.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  ) : (
                    <span className="block w-4" />
                  )}
                </TableCell>
              )}
              <TableCell className="whitespace-nowrap text-sm text-gray-600">
                {service.id}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {service.status === 'approved' && (
                  <Badge variant={service.is_public ? 'success' : 'warning'} size="sm">
                    {service.is_public ? '공개' : '비공개'}
                  </Badge>
                )}
                {service.status !== 'approved' && '-'}
              </TableCell>
              <TableCell className="whitespace-nowrap">{service.category ? (CATEGORY_LABELS[service.category as ServiceCategory] || service.category) : '-'}</TableCell>
              <TableCell className="min-w-[150px]">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">{service.service_slug}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">{getServiceIcon(service.metadata, service.category)}</span>
                    <span
                      className="font-medium cursor-pointer hover:text-blue-600"
                      onClick={() => onServiceClick?.(service)}
                    >
                      {service.service_name}
                    </span>
                  </div>
                </div>
              </TableCell>
              <TableCell
                className="max-w-xs"
                title={service.service_description || undefined}
              >
                <div className="line-clamp-2 text-sm text-gray-600">
                  {service.service_description || '-'}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap">{service.contact_name || '-'}</TableCell>
              <TableCell className="whitespace-nowrap">
                <a
                  href={service.service_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 underline"
                  title={service.service_url}
                >
                  이동
                </a>
              </TableCell>
              <TableCell className="whitespace-nowrap">{service.version || '-'}</TableCell>
              <TableCell className="whitespace-nowrap">{getServiceStatusBadge(service.status)}</TableCell>
              <TableCell className="whitespace-nowrap">
                {getHealthBadge(healthStatuses[service.id])}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {formatDate(service.created_at)}
              </TableCell>
              {!isReadOnly && (
                <TableCell>
                  <div className="flex gap-1">
                    {service.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onApproveClick(service.id)}
                        >
                          승인
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onReject(service.id, service.service_name)}
                        >
                          거부
                        </Button>
                      </>
                    )}
                    {(service.status === 'approved' || service.status === 'suspended') && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onManagePlans?.(service)}
                          title="플랜"
                          className="px-2"
                        >
                          <CreditCard className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onManageClient?.(service)}
                          title="로그인 연동"
                          className="px-2"
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        {service.status === 'approved' ? (
                          <Button
                            size="sm"
                            variant="warning"
                            onClick={() => onSuspend(service.id, service.service_name)}
                            title="일시중지"
                            className="px-2"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onResume(service.id, service.service_name)}
                            title="재개"
                            className="px-2 bg-white"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={service.is_public ? 'outline' : 'default'}
                          onClick={() => onToggleVisibility(service.id, service.service_name, !service.is_public)}
                          title={service.is_public ? '비공개' : '공개'}
                          className={`px-2 ${!service.is_public ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                        >
                          {service.is_public ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onDelete(service.id, service.service_name)}
                          title="삭제"
                          className="px-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {service.status === 'deleted' && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onRestore?.(service.id, service.service_name)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        클리어
                      </Button>
                    )}
                    {service.status === 'rejected' && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onCancelRejection?.(service.id, service.service_name)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        클리어
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
